"use client";

import { requestSubscriptionEditing } from "./billing/subscription-access";

import { nanoid } from "nanoid";
import { create } from "zustand";
import { getDefinition } from "./catalog";
import { getLayoutEngine } from "./cable-router/active-router";
import { terminalOverrideUpdates, applyTerminalOverrideUpdates } from "./cable-router/layout-engine";
import { useFeatureFlags } from "./feature-flags";
import { chargeCurrentMaximum, configuredChargeCurrent } from "./charge-current";
import { planCableConfirmation, type ConfirmConnectionOptions } from "./confirm-cable";
export type { ConfirmConnectionOptions };
import { absolutePosition } from "./auto-fuse";
import { planBusbarToJunction, planJunctionToBusbar } from "./busbar-conversion";
import { planCableAddition } from "./cable-add";
import { refreshAutoFuses } from "./cable-spec";
import { DESIGN_PROFILES, resolveDesignProfile } from "./design-profiles";
import { planComponentReplace } from "./component-replace";
import { planLegacyMicrowaveAcConversion } from "./legacy-microwave";
import { planComponentCustomization } from "./component-customize";
import { configureInlineFuse } from "./custom-inline-fuse";
import { configureDcFuseBlock } from "./custom-dc-fuse-block";
import type { FuseType } from "./types";
import { recordFirstValue } from "./first-touch";
import { categorySpecDefaults } from "./custom-component";
import { planCableSplit, type SplitCableParams } from "./cable-split";
import { planFreezeBuild, planUnfreezeBuild } from "./freeze-build";
import {
  cloneNodesAndCables,
  getClipboard,
  planClipboardPaste,
  snapshotSelectionToClipboard,
} from "./clipboard";
import {
  cablesThroughNodes,
} from "./cable-tidy";
import { migrate } from "./schema/migrate";
import { SCHEMA_VERSION } from "./schema/version";
import {
  captureAnalytics,
  createCheckoutAttemptId,
  hasReachedValueMoment,
  type EquipmentLimitAnalyticsContext,
} from "./analytics";
import { useAuthStore } from "./auth/store";
import {
  EQUIPMENT_COUNT_POLICY_VERSION,
  FREE_EQUIPMENT_LIMIT,
  countEquipment,
  crossedEquipmentMilestones,
  equipmentChangeExceedsLimit,
  equipmentNodeExemption,
  summarizeEquipment,
  type EquipmentExemption,
  type EquipmentLimitAttempt,
  type EquipmentLimitOperation,
  type EquipmentMutationSource,
} from "./equipment-limit";
import { equipmentLimitRuntime } from "./equipment-limit-runtime";
import { clearEquipmentLimitAuthHandoff } from "./equipment-limit-attempt-storage";
import { useAutoSizeVdropTarget } from "./settings/auto-size-vdrop-target";
import { runAllDiagnostics } from "./diagnostics";
import { canReviewIssue, issueReviewFingerprint, issueReviewKey } from "./diagnostics/reviews";
import type { Issue } from "./types";
import {
  cloneSystem,
  emptySystem,
  expandCableTidyScope,
  filterSelectionToAlive,
  mergeCablesAcrossDeletedJunctions,
  pruneOrphanedJunctions,
  pruneStaleWaypointsForNode,
  sameIds,
} from "./store-helpers";
import { findNode } from "./system-lookup";
import { optimalTerminalOverrides } from "./terminal-snap";
import { planZoneReparent } from "./zone-reparent";
import { planZoneDistanceRelength, zonePairKey } from "./zone-lengths";
import {
  type Cable,
  type ComponentDefinition,
  type ComponentNode,
  type System,
  type SystemNode,
  type ZoneNode,
} from "./types";
import {
  createSimulateActions,
  initialSimulateState,
  type SimulateState,
} from "./simulate-store-slice";

export type { SimulateState };

export { SCHEMA_VERSION };

/** Lives in `zone-lengths.ts` so the edge-bundled AI planner can import it without the store. */
export { zonePairKey };

function equipmentAnalyticsContext(
  system: System,
  journeyId: string,
): EquipmentLimitAnalyticsContext {
  const summary = summarizeEquipment(system.nodes);
  const runtime = equipmentLimitRuntime();
  const auth = useAuthStore.getState();
  return {
    count_policy_version: EQUIPMENT_COUNT_POLICY_VERSION,
    // The served arm decides the ceiling; an unlimited arm reports the control
    // constant so the analytics column stays a number.
    equipment_limit: Number.isFinite(runtime.limit) ? runtime.limit : FREE_EQUIPMENT_LIMIT,
    equipment_count: summary.equipment,
    remaining_equipment: Math.max(0, (Number.isFinite(runtime.limit) ? runtime.limit : FREE_EQUIPMENT_LIMIT) - summary.equipment),
    authenticated: Boolean(auth.user),
    entitled: runtime.entitled,
    limit_enforced: runtime.enforced,
    monetization_enabled: runtime.monetizationEnabled,
    equipment_limit_journey_id: journeyId,
  };
}

/** Writing back the same number is not a measurement, so the AI's guess survives it. */
function clearLengthGuess(before: Cable, after: Cable): Cable {
  if (!after.lengthEstimated) return after;
  if (after.lengthM === before.lengthM) return after;
  const { lengthEstimated: _guessed, ...rest } = after;
  return rest as Cable;
}

/** `setCustomCategory` zeroes the ones the new category doesn't declare, so a
 *  battery's `capacityAh` can't leak into a load's energy budget. */
const OPTIONAL_SPEC_FIELDS = [
  "capacityAh",
  "voltage",
  "watts",
  "ampDraw",
  "ampOutput",
  "ampSurge",
  "peakSunHoursDefault",
  "chargeAmpsTypical",
  "chemistry",
  "maxDischargeA",
  "maxChargeA",
  "recommendedChargeA", "chargePlanningA", "chargerMethodChargeA", "chargingNotes", "coldCrankingAmps", "marineCrankingAmps",
  "shortCircuitA",
  "pvMaxVoc",
  "pvVoc",
  "dutyHoursPerDay",
  "alwaysOn",
] as const satisfies ReadonlyArray<keyof ComponentDefinition>;

const CATEGORY_SPEC_FIELDS: Record<
  ComponentDefinition["category"],
  ReadonlySet<(typeof OPTIONAL_SPEC_FIELDS)[number]>
> = {
  battery: new Set([
    "capacityAh",
    "voltage",
    "chemistry",
    "maxDischargeA",
    "maxChargeA",
    "recommendedChargeA", "chargePlanningA", "chargerMethodChargeA", "chargingNotes", "coldCrankingAmps", "marineCrankingAmps",
    "shortCircuitA",
  ]),
  charger: new Set(["ampOutput", "chargeAmpsTypical", "voltage"]),
  solar: new Set(["watts", "voltage", "pvVoc", "pvMaxVoc", "peakSunHoursDefault"]),
  source: new Set(["ampOutput", "voltage", "chargeAmpsTypical"]),
  engine: new Set([]),
  distribution: new Set([]),
  load: new Set(["ampDraw", "watts", "ampSurge", "dutyHoursPerDay", "alwaysOn", "voltage"]),
  monitor: new Set(["ampDraw"]),
};

export type PendingConnection = {
  fromNodeId: string;
  fromTerminalId: string;
  toNodeId: string;
  toTerminalId: string;
};

export type Selection = {
  nodeIds: string[];
  cableIds: string[];
};

export type SplitCableWithJunctionParams = SplitCableParams;

type Snapshot = { system: System };

export type AppMode = "edit" | "simulate";

interface State {
  system: System;
  equipmentLimitAttempt: EquipmentLimitAttempt | null;
  equipmentLimitLastHit: {
    checkoutAttemptId: string;
    at: number;
  } | null;
  equipmentLimitJourneyId: string;
  selection: Selection;
  pendingConnection: PendingConnection | null;
  inspectorOpen: boolean;
  paletteOpen: boolean;
  mode: AppMode;
  simulateState: SimulateState;

  past: Snapshot[];
  future: Snapshot[];

  addComponent(
    definitionId: string,
    position: { x: number; y: number },
    customDef?: ComponentDefinition,
    source?: EquipmentMutationSource,
  ): string;
  clearEquipmentLimitAttempt(options?: { clearLastHit?: boolean }): void;
  restoreEquipmentLimitAttempt(attempt: EquipmentLimitAttempt): boolean;
  dismissEquipmentLimitAttempt(): void;
  trackEquipmentProjectRestart(source: "new_project"): void;
  addZone(position: { x: number; y: number }, opts?: Partial<ZoneNode>): string;
  addJunction(position: { x: number; y: number }, opts?: { provisional?: boolean }): string;
  /** With `relength`, every crossing cable moves by the *difference* between the
   *  old distance and the new one — never onto one shared number. */
  setZoneDistance(
    zoneAId: string,
    zoneBId: string,
    distanceM: number,
    opts?: { relength?: boolean },
  ): void;
  removeZoneDistance(zoneAId: string, zoneBId: string): void;
  updateNode(id: string, partial: Partial<SystemNode>): void;
  /** Changes a live charging ceiling without changing hardware or cable ratings. */
  setChargeCurrentLimit(id: string, limitA: number | undefined): void;
  /** Converts the node to a "custom" instance if it isn't one already.
   *  `undefined` values in `partial` are ignored — a field is only removed
   *  through `opts.unset`, so a controlled input that momentarily reports
   *  "empty" can never delete a spec. */
  customizeComponent(
    id: string,
    partial: Partial<ComponentDefinition>,
    opts?: { unset?: readonly (keyof ComponentDefinition)[] },
  ): void;
  /** Clears the spec fields the new category doesn't declare. */
  setCustomCategory(id: string, category: ComponentDefinition["category"]): void;
  revertComponent(id: string): void;
  removeNodes(ids: string[]): void;
  setNodePosition(id: string, position: { x: number; y: number }, parentId?: string | null): void;
  /** Per-frame drag write; callers must call `finalizeNodeDrag(id)` on drag stop. */
  dragNodePosition(id: string, position: { x: number; y: number }): void;
  finalizeNodeDrag(id: string): void;
  setNodeParent(id: string, parentId: string | null): void;
  /** A node's zone is decided by its centre; its position is re-based so it doesn't jump. */
  reparentNodesToZones(ids: string[]): void;

  addCable(cable: Omit<Cable, "id">): string;
  updateCable(id: string, partial: Partial<Cable>): void;
  /** Re-aims the attached terminals in the same commit. Doesn't push history. */
  updateCableWaypoints(id: string, waypoints: Cable["waypoints"]): void;
  /** Re-bakes the auto fuse; a manual one (`fuseAmpAuto === false`) is left alone.
   *  Doesn't push history — callers snapshot first. */
  repointCable(
    id: string,
    endpoints: Pick<Cable, "fromNodeId" | "fromTerminalId" | "toNodeId" | "toTerminalId">,
  ): void;
  removeCables(ids: string[]): void;
  resetCableLayouts(ids: string[]): void;
  /** Arrange components and redraw all cables as one undo step. Flag-gated. */
  autoLayoutComponents(ids: string[], opts?: { skipHistory?: boolean }): void;
  /** Pushes one history entry unless `skipHistory` folds it into the caller's step. */
  tidyCableLayouts(ids: string[], opts?: { skipHistory?: boolean }): void;
  /** Includes cables on zone descendants — moving a zone moves its children. */
  tidyCableLayoutsForNodes(
    nodeIds: string[],
    opts?: { skipHistory?: boolean },
  ): void;
  /** Tidies the coupling set too — same-terminal and same-junction-side siblings.
   *  Prefer it when one cable changed, so the node's others keep their hand routing. */
  tidyCableLayoutsForCables(
    cableIds: string[],
    opts?: { skipHistory?: boolean },
  ): void;

  setSelection(selection: Partial<Selection>): void;
  clearSelection(): void;
  selectAll(): void;
  /** A cable comes along only when both its endpoints are in the selection. */
  duplicateSelection(): { nodeIds: string[]; cableIds: string[] };
  lastCopyAt: number | null;
  copySelection(): void;
  pasteFromClipboard(): { nodeIds: string[]; cableIds: string[] };
  /** Does not push history. */
  nudgeSelection(dx: number, dy: number): void;

  setPendingConnection(p: PendingConnection | null): void;
  cancelPendingConnection(): void;
  confirmPendingConnection(opts: ConfirmConnectionOptions): string[] | null;

  /** Also attaches the dragged terminal to the new junction. One history step. */
  splitCableWithJunction(
    params: SplitCableWithJunctionParams,
  ): { junctionId: string; newCableId: string } | null;

  /** Doesn't push history — it rides on the action that changed the cable. */
  recomputeTerminalsForCable(cableId: string): void;
  /** Batched `recomputeTerminalsForCable`, one commit. Doesn't push history. */
  recomputeTerminalsForCables(cableIds: Iterable<string>): void;

  /** Pinned overrides survive auto-reaim. Doesn't push history — the caller
   *  pushes at drag start. */
  setTerminalOverride(
    nodeId: string,
    terminalId: string,
    override: { side: "left" | "right" | "top" | "bottom"; offset: number; pinned?: boolean },
  ): void;

  clearTerminalOverride(nodeId: string, terminalId: string): void;

  setInspectorOpen(open: boolean): void;
  setPaletteOpen(open: boolean): void;
  mobileScenarioOpen: boolean;
  setMobileScenarioOpen(open: boolean): void;
  /** Opened only by the Details pill — never automatically by a selection change. */
  mobileDetailsOpen: boolean;
  setMobileDetailsOpen(open: boolean): void;
  setMode(mode: AppMode): void;
  toggleMode(): void;
  setComponentSimulateState(nodeId: string, optionId: string): void;
  setBatterySoc(nodeId: string, soc: number): void;
  setLoadActive(nodeId: string, active: boolean): void;

  loadSystem(
    s: System,
    opts?: { enforceEquipmentLimit?: boolean; source?: EquipmentMutationSource; preserveLayout?: boolean },
  ): boolean;
  /** One undo step; unlike `loadSystem`, keeps the undo stack and the open project. */
  applyAiSystemEdit(
    next: System,
    added: { nodeIds: string[]; cableIds: string[] },
  ): boolean;
  newSystem(): void;
  setSystemName(name: string): void;
  /** Project-scoped electrical rules. Recomputes automatic choices in one undo step. */
  setDesignProfile(id: string): void;
  setIssueReviewed(issue: Issue, reviewed: boolean): void;

  /** Pins every wire size (and fused cables' rating) to its current auto value. */
  freezeBuild(): { wiresFrozen: number; fusesRefreshed: number };

  /** Clears wire-size overrides only — fuse ratings are left alone. */
  unfreezeBuild(): { wiresCleared: number };

  /** A no-op when the new part can't take every attached cable — offer only what
   *  `replacementOptions` returns. */
  replaceComponentDefinition(nodeId: string, definitionId: string): void;
  replaceLegacyMicrowaveWithAc(nodeId: string, voltage: 120 | 230): void;
  configureCustomInlineFuse(nodeId: string, rating: number, family: FuseType): void;
  configureCustomDcFuseBlock(nodeId: string, family: FuseType): void;

  /** The first cable becomes the input; the rest spread across studs. */
  convertJunctionToBusbar(
    nodeId: string,
    polarity: "positive" | "negative" | "ground",
  ): void;

  /** A positive busbar with `isFusebox` set. One history step. */
  convertJunctionToFusebox(nodeId: string): void;

  /** Every terminal on the node collapses to the single "j" terminal. */
  convertBusbarToJunction(nodeId: string): void;

  undo(): void;
  redo(): void;
  pushHistory(): void;
}

export const useSystemStore = create<State>((set, get) => {
  const historied = <T>(body: () => T): T => {
    get().pushHistory();
    return body();
  };

  const recordEquipmentChange = (
    before: System,
    after: System,
    operation: EquipmentLimitOperation,
    source: EquipmentMutationSource,
  ) => {
    const previousCount = countEquipment(before);
    const nextCount = countEquipment(after);
    if (previousCount === nextCount) return;
    const mutationId = nanoid(12);
    const context = equipmentAnalyticsContext(after, get().equipmentLimitJourneyId);
    captureAnalytics("equipment_count_changed", {
      ...context,
      mutation_id: mutationId,
      operation,
      mutation_source: source,
      previous_equipment_count: previousCount,
      equipment_delta: nextCount - previousCount,
      successful: true,
    });
    const projectKey = context.equipment_limit_journey_id;
    for (const milestone of crossedEquipmentMilestones(previousCount, nextCount)) {
      captureAnalytics("equipment_limit_milestone_reached", {
        ...context,
        mutation_id: mutationId,
        mutation_source: source,
        milestone,
        crossed_by_delta: nextCount - previousCount,
        $insert_id: `${EQUIPMENT_COUNT_POLICY_VERSION}:${projectKey}:milestone:${milestone}`,
      });
    }
  };

  const recordExemptAddition = (
    system: System,
    itemKind: EquipmentExemption | "wire",
    source: EquipmentMutationSource,
    addedCount = 1,
  ) => {
    const context = equipmentAnalyticsContext(system, get().equipmentLimitJourneyId);
    captureAnalytics("equipment_limit_exempt_item_added", {
      ...context,
      mutation_id: nanoid(12),
      mutation_source: source,
      item_kind: itemKind,
      added_count: addedCount,
      at_limit: context.equipment_count >= context.equipment_limit,
    });
  };

  const recordExemptNodes = (
    nodes: SystemNode[],
    system: System,
    source: EquipmentMutationSource,
  ) => {
    const added = summarizeEquipment(nodes);
    if (added.freeFuses) {
      recordExemptAddition(system, "fuse", source, added.freeFuses);
    }
    if (added.freeBusbars) {
      recordExemptAddition(system, "busbar", source, added.freeBusbars);
    }
    if (added.layoutNodes) {
      recordExemptAddition(system, "layout", source, added.layoutNodes);
    }
  };

  const equipmentChangeIsBlocked = (args: {
    nextNodes: SystemNode[];
    operation: EquipmentLimitOperation;
    source: EquipmentMutationSource;
    attemptedDefinition?: ComponentDefinition;
    attemptedDefinitionId?: string;
  }): boolean => {
    const runtime = equipmentLimitRuntime();
    if (!runtime.enforced) return false;
    const currentSystem = get().system;
    const currentCount = countEquipment(currentSystem);
    const nextCount = summarizeEquipment(args.nextNodes).equipment;
    if (!equipmentChangeExceedsLimit(currentCount, nextCount, runtime.limit)) return false;

    const attempt: EquipmentLimitAttempt = {
      checkoutAttemptId: createCheckoutAttemptId(),
      mutationId: nanoid(12),
      source: args.source,
      operation: args.operation,
      currentCount,
      attemptedDelta: Math.max(1, nextCount - currentCount),
      wouldBeCount: nextCount,
      attemptedDefinitionId:
        args.attemptedDefinitionId === "custom"
          ? "custom"
          : args.attemptedDefinitionId ?? null,
      attemptedCategory: args.attemptedDefinition?.category ?? "unknown",
      createdAt: Date.now(),
    };
    set({
      equipmentLimitAttempt: attempt,
      equipmentLimitLastHit: {
        checkoutAttemptId: attempt.checkoutAttemptId,
        at: attempt.createdAt,
      },
    });
    captureAnalytics("equipment_limit_blocked", {
      ...equipmentAnalyticsContext(currentSystem, get().equipmentLimitJourneyId),
      mutation_id: attempt.mutationId,
      checkout_attempt_id: attempt.checkoutAttemptId,
      operation: attempt.operation,
      mutation_source: attempt.source,
      attempted_equipment_delta: attempt.attemptedDelta,
      would_be_equipment_count: attempt.wouldBeCount,
      attempted_category: attempt.attemptedCategory,
      value_moment_reached: hasReachedValueMoment(currentSystem),
    });
    return true;
  };

  return {
  system: emptySystem(),
  equipmentLimitAttempt: null,
  equipmentLimitLastHit: null,
  equipmentLimitJourneyId: nanoid(12),
  selection: { nodeIds: [], cableIds: [] },
  pendingConnection: null,
  inspectorOpen: true,
  paletteOpen: true,
  lastCopyAt: null,
  mobileScenarioOpen: false,
  mobileDetailsOpen: false,
  mode: "edit",
  simulateState: initialSimulateState(),
  past: [],
  future: [],

  clearEquipmentLimitAttempt: (options) => {
    const attempt = get().equipmentLimitAttempt;
    const lastHit = get().equipmentLimitLastHit;
    const handoffId =
      attempt?.checkoutAttemptId ??
      (options?.clearLastHit ? lastHit?.checkoutAttemptId : null);
    if (handoffId) {
      clearEquipmentLimitAuthHandoff({ checkoutAttemptId: handoffId });
    }
    set({
      equipmentLimitAttempt: null,
      equipmentLimitLastHit:
        options?.clearLastHit ||
        (attempt && lastHit?.checkoutAttemptId === attempt.checkoutAttemptId)
          ? null
          : lastHit,
    });
  },
  restoreEquipmentLimitAttempt: (attempt) => {
    const currentCount = countEquipment(get().system);
    if (
      !equipmentChangeExceedsLimit(
        currentCount,
        currentCount + attempt.attemptedDelta,
        equipmentLimitRuntime().limit,
      )
    ) {
      clearEquipmentLimitAuthHandoff({
        checkoutAttemptId: attempt.checkoutAttemptId,
      });
      return false;
    }
    set({
      equipmentLimitAttempt: attempt,
      equipmentLimitLastHit: {
        checkoutAttemptId: attempt.checkoutAttemptId,
        at: attempt.createdAt,
      },
    });
    return true;
  },
  dismissEquipmentLimitAttempt: () => {
    const attempt = get().equipmentLimitAttempt;
    if (!attempt) return;
    captureAnalytics("equipment_limit_paywall_dismissed", {
      ...equipmentAnalyticsContext(get().system, get().equipmentLimitJourneyId),
      checkout_attempt_id: attempt.checkoutAttemptId,
      seconds_visible: Math.max(0, (Date.now() - attempt.createdAt) / 1000),
    });
    clearEquipmentLimitAuthHandoff({
      checkoutAttemptId: attempt.checkoutAttemptId,
    });
    set({ equipmentLimitAttempt: null });
  },
  trackEquipmentProjectRestart: (source) => {
    const system = get().system;
    const lastHit = get().equipmentLimitLastHit;
    if (system.nodes.length === 0 && !lastHit) return;
    captureAnalytics("equipment_project_restarted", {
      ...equipmentAnalyticsContext(system, get().equipmentLimitJourneyId),
      restart_source: source,
      previous_equipment_count: countEquipment(system),
      after_limit_hit: lastHit !== null,
      checkout_attempt_id: lastHit?.checkoutAttemptId ?? null,
      seconds_since_limit_hit: lastHit
        ? Math.max(0, (Date.now() - lastHit.at) / 1000)
        : null,
    });
    if (lastHit) {
      clearEquipmentLimitAuthHandoff({
        checkoutAttemptId: lastHit.checkoutAttemptId,
      });
      set({
        equipmentLimitAttempt: null,
        equipmentLimitLastHit: null,
      });
    }
  },

  pushHistory: () => {
    if (!requestSubscriptionEditing()) return;
    const { system, past } = get();
    set({ past: [...past.slice(-49), { system: cloneSystem(system) }], future: [] });
  },

  addComponent: (definitionId, position, customDef, source = "unknown") => {
    if (!requestSubscriptionEditing()) return "";
    const def = getDefinition(definitionId, customDef);
    if (!def) {
      console.warn("Unknown definition", definitionId);
      return "";
    }
    const id = nanoid(8);
    const node: ComponentNode = {
      id,
      kind: "component",
      definitionId,
      customDefinition: definitionId === "custom" ? customDef : undefined,
      position,
      ...(def.defaultFuseAmp != null && { fuseAmp: def.defaultFuseAmp }),
      // Studs always equal connected cables; a fresh bar starts at one.
      ...(def.renderVariant === "busbar" ? { studCount: 1 } : {}),
    };
    const before = get().system;
    if (
      equipmentChangeIsBlocked({
        nextNodes: [...before.nodes, node],
        operation: "add",
        source,
        attemptedDefinition: def,
        attemptedDefinitionId: definitionId,
      })
    ) {
      return "";
    }
    recordFirstValue("canvas");
    const addedId = historied(() => {
      set((s) => ({
        system: { ...s.system, nodes: [...s.system.nodes, node] },
      }));
      if (source === "ai_component" && useFeatureFlags.getState().flags.componentAutoLayout) {
        get().autoLayoutComponents([id], { skipHistory: true });
      } else {
        // The new body is an obstacle even when automatic placement is off.
        get().tidyCableLayouts(get().system.cables.map(c => c.id), { skipHistory: true });
      }
      return id;
    });
    const after = get().system;
    const exemption = equipmentNodeExemption(node);
    if (exemption) recordExemptAddition(after, exemption, source);
    else recordEquipmentChange(before, after, "add", source);
    return addedId;
  },

  addZone: (position, opts) => {
    if (!requestSubscriptionEditing()) return "";
    const id = historied(() => {
      const id = nanoid(8);
      const node: ZoneNode = {
        id,
        kind: "zone",
        position,
        width: opts?.width ?? 480,
        height: opts?.height ?? 320,
        label: opts?.label ?? "Zone",
        color: opts?.color,
      };
      set((s) => ({ system: { ...s.system, nodes: [node, ...s.system.nodes] } }));
      return id;
    });
    recordExemptAddition(get().system, "layout", "unknown");
    return id;
  },

  setZoneDistance: (a, b, distanceM, opts) => {
    if (!requestSubscriptionEditing()) return;
    if (a === b) return;
    const key = zonePairKey(a, b);
    const cur = get().system.zoneDistances?.[key];
    if (cur === distanceM) return;
    // Planned before the write — the plan diffs against the *old* distance.
    const relengths = opts?.relength
      ? planZoneDistanceRelength(get().system, a, b, distanceM)
      : [];
    const moved = new Map(relengths.map((r) => [r.id, r.lengthM]));
    historied(() => {
      set((s) => {
        const relengthed = moved.size
          ? s.system.cables.map((c) =>
              moved.has(c.id)
                ? clearLengthGuess(c, { ...c, lengthM: moved.get(c.id)! })
                : c,
            )
          : s.system.cables;
        const system = {
          ...s.system,
          zoneDistances: { ...(s.system.zoneDistances ?? {}), [key]: distanceM },
          cables: relengthed,
        };
        return {
          system: moved.size
            ? { ...system, cables: refreshAutoFuses(system) }
            : system,
        };
      });
    });
  },

  removeZoneDistance: (a, b) => {
    if (!requestSubscriptionEditing()) return;
    if (a === b) return;
    const key = zonePairKey(a, b);
    const distances = get().system.zoneDistances ?? {};
    if (!(key in distances)) return;
    historied(() => {
      set((s) => {
        const next = { ...(s.system.zoneDistances ?? {}) };
        delete next[key];
        return { system: { ...s.system, zoneDistances: next } };
      });
    });
  },

  addJunction: (position, opts) => {
    if (!requestSubscriptionEditing()) return "";
    const id = historied(() => {
      const id = nanoid(8);
      const node: ComponentNode = {
        id,
        kind: "component",
        definitionId: "junction",
        position,
        ...(opts?.provisional ? { provisional: true } : {}),
      };
      set((s) => ({ system: { ...s.system, nodes: [...s.system.nodes, node] } }));
      return id;
    });
    recordExemptAddition(get().system, "layout", "unknown");
    return id;
  },

  updateNode: (id, partial) => {
    if (!requestSubscriptionEditing()) return;
    set((s) => {
      const nodes = s.system.nodes.map((n) =>
        n.id === id ? ({ ...n, ...partial } as SystemNode) : n,
      );
      const cables =
        "terminalOverrides" in (partial as Record<string, unknown>)
          ? pruneStaleWaypointsForNode(nodes, s.system.cables, id)
          : s.system.cables;
      return { system: { ...s.system, nodes, cables } };
    });
  },

  setChargeCurrentLimit: (id, limitA) => {
    if (!requestSubscriptionEditing()) return;
    const system = get().system;
    const node = findNode(system, id);
    if (node?.kind !== "component") return;
    const def = getDefinition(node.definitionId, node.customDefinition);
    const maximum = def && chargeCurrentMaximum(def);
    if (maximum === undefined || (limitA !== undefined &&
      (!Number.isFinite(limitA) || limitA < 0 || limitA > maximum)) || node.chargeCurrentLimitA === limitA) return;
    historied(() => set({ system: { ...system, nodes: system.nodes.map(n =>
      n.id === id ? { ...node, chargeCurrentLimitA: limitA } : n) } }));
  },

  customizeComponent: (id, partial, opts) => {
    if (!requestSubscriptionEditing()) return;
    const before = get().system;
    const { nodes, electricalChanged } = planComponentCustomization(
      before, id, partial, opts,
    );
    const changedNode = nodes.find((node) => node.id === id);
    if (
      equipmentChangeIsBlocked({
        nextNodes: nodes,
        operation: "replace",
        source: "customize",
        attemptedDefinition:
          changedNode?.kind === "component"
            ? getDefinition(changedNode.definitionId, changedNode.customDefinition)
            : undefined,
        attemptedDefinitionId:
          changedNode?.kind === "component" ? changedNode.definitionId : undefined,
      })
    ) {
      return;
    }
    // The cached fuse rating is the only derived value that goes stale.
    const tentativeSystem: System = { ...before, nodes };
    const cables = electricalChanged
      ? refreshAutoFuses(tentativeSystem)
      : before.cables;
    const after = { ...tentativeSystem, cables };
    set({ system: after });
    recordEquipmentChange(before, after, "replace", "customize");
    if (changedNode) {
      const beforeNode = before.nodes.find((node) => node.id === id);
      const exemption = equipmentNodeExemption(changedNode);
      if (
        exemption &&
        beforeNode &&
        equipmentNodeExemption(beforeNode) !== exemption
      ) {
        recordExemptAddition(after, exemption, "customize");
      }
    }
  },

  setCustomCategory: (id, category) => {
    if (!requestSubscriptionEditing()) return;
    const node = findNode(get().system, id);
    if (!node || node.kind !== "component") return;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def || def.category === category) return;
    const keep = CATEGORY_SPEC_FIELDS[category];
    const partial: Partial<ComponentDefinition> = { category };
    const unset = OPTIONAL_SPEC_FIELDS.filter(
      (field) => !keep.has(field) && def[field] != null,
    );
    // Changing a placeholder's drawing category cannot establish its ratings.
    const seeds = def.requestedModel ? {} : categorySpecDefaults(category);
    for (const [field, value] of Object.entries(seeds)) {
      if ((def as Record<string, unknown>)[field] == null) {
        (partial as Record<string, unknown>)[field] = value;
      }
    }
    get().customizeComponent(id, partial, { unset });
  },

  revertComponent: (id) => {
    if (!requestSubscriptionEditing()) return;
    const node = findNode(get().system, id);
    if (!node || node.kind !== "component") return;
    if (node.customDefinition?.requestedModel) return;
    const origId = node.customDefinition?.id;
    const originalDef = origId && origId !== "custom" ? getDefinition(origId) : undefined;
    if (!originalDef) return;
    const before = get().system;
    historied(() => {
      set((s) => ({
        system: {
          ...s.system,
          nodes: s.system.nodes.map((n) =>
            n.id === id && n.kind === "component"
              ? { ...n, definitionId: originalDef.id, customDefinition: undefined,
                ...(n.chargeCurrentLimitA === undefined ? {} : { chargeCurrentLimitA: configuredChargeCurrent(n, originalDef) }) }
              : n,
          ),
        },
      }));
    });
    const after = get().system;
    recordEquipmentChange(before, after, "replace", "revert");
    const reverted = after.nodes.find((node) => node.id === id);
    const exemption = reverted ? equipmentNodeExemption(reverted) : null;
    const beforeNode = before.nodes.find((node) => node.id === id);
    if (
      exemption &&
      beforeNode &&
      equipmentNodeExemption(beforeNode) !== exemption
    ) {
      recordExemptAddition(after, exemption, "revert");
    }
  },

  removeNodes: (ids) => {
    if (!requestSubscriptionEditing()) return;
    if (ids.length === 0) return;
    const before = get().system;
    historied(() => {
    const idSet = new Set(ids);
    set((s) => {
      const { cables: mergedCables, consumedCableIds } = mergeCablesAcrossDeletedJunctions(
        s.system.nodes,
        s.system.cables,
        idSet,
      );
      // A dangling `parentId` makes React Flow warn and draw the child in the wrong place.
      const reparentedNodes = s.system.nodes.map((n) => {
        if (idSet.has(n.id)) return n;
        if (!n.parentId || !idSet.has(n.parentId)) return n;
        const abs = absolutePosition(n, s.system.nodes);
        const { parentId: _drop, ...rest } = n;
        void _drop;
        return { ...rest, position: abs } as SystemNode;
      });
      const baseNodes = reparentedNodes.filter((n) => !idSet.has(n.id));
      const baseCables = mergedCables.filter(
        (c) =>
          !idSet.has(c.fromNodeId) &&
          !idSet.has(c.toNodeId) &&
          !consumedCableIds.has(c.id),
      );
      const { nodes, cables } = pruneOrphanedJunctions(baseNodes, baseCables);
      return {
        system: { ...s.system, nodes, cables },
        selection: filterSelectionToAlive(s.selection, nodes, cables),
      };
    });
    // Removal freed space; same history step, so one undo restores nodes and routes.
    get().tidyCableLayouts(
      get().system.cables.map((c) => c.id),
      { skipHistory: true },
    );
    });
    recordEquipmentChange(before, get().system, "remove", "unknown");
  },

  setNodePosition: (id, position, parentId) => {
    if (!requestSubscriptionEditing()) return;
    set((s) => {
      const nodes = s.system.nodes.map((n) =>
        n.id === id
          ? ({
              ...n,
              position,
              parentId:
                parentId === undefined
                  ? n.parentId
                  : parentId === null
                    ? undefined
                    : parentId,
            } as SystemNode)
          : n,
      );
      const cables = pruneStaleWaypointsForNode(nodes, s.system.cables, id);
      return { system: { ...s.system, nodes, cables } };
    });
  },

  dragNodePosition: (id, position) => {
    if (!requestSubscriptionEditing()) return;
    set((s) => ({
      system: {
        ...s.system,
        nodes: s.system.nodes.map((n) =>
          n.id === id ? ({ ...n, position } as SystemNode) : n,
        ),
      },
    }));
  },

  finalizeNodeDrag: (id) => {
    if (!requestSubscriptionEditing()) return;
    const { system } = get();
    const node = findNode(system, id);
    if (!node) return;
    let overrides: Record<string, { side: "left" | "right" | "top" | "bottom"; offset: number }> = {};
    if (node.kind === "component") {
      const def = getDefinition(node.definitionId, node.customDefinition);
      if (def) {
        overrides = optimalTerminalOverrides({
          node,
          def,
          nodes: system.nodes,
          cables: system.cables,
          newAbsPos: absolutePosition(node, system.nodes),
        });
      }
    }
    const hasOverrides = Object.keys(overrides).length > 0;
    set((s) => {
      const nodes = hasOverrides
        ? s.system.nodes.map((n) =>
            n.id === id && n.kind === "component"
              ? ({
                  ...n,
                  terminalOverrides: { ...(n.terminalOverrides ?? {}), ...overrides },
                } as SystemNode)
              : n,
          )
        : s.system.nodes;
      const cables = pruneStaleWaypointsForNode(nodes, s.system.cables, id);
      if (nodes === s.system.nodes && cables === s.system.cables) return s;
      return { system: { ...s.system, nodes, cables } };
    });
  },

  setNodeParent: (id, parentId) => {
    if (!requestSubscriptionEditing()) return;
    set((s) => ({
      system: {
        ...s.system,
        nodes: s.system.nodes.map((n) =>
          n.id === id
            ? ({ ...n, parentId: parentId ?? undefined } as SystemNode)
            : n,
        ),
      },
    }));
  },

  reparentNodesToZones: (ids) => {
    if (!requestSubscriptionEditing()) return;
    const changes = planZoneReparent(get().system, ids);
    if (changes.length === 0) return;
    const byId = new Map(changes.map((c) => [c.id, c]));
    set((s) => ({
      system: {
        ...s.system,
        // No waypoint prune: re-basing leaves every absolute position untouched.
        nodes: s.system.nodes.map((n) => {
          const c = byId.get(n.id);
          return c
            ? ({
                ...n,
                position: c.position,
                parentId: c.parentId ?? undefined,
              } as SystemNode)
            : n;
        }),
      },
    }));
  },

  addCable: (cable) => {
    if (!requestSubscriptionEditing()) return "";
    const id = historied(() => {
      const id = nanoid(8);
      set((s) => {
        const plan = planCableAddition({ ...cable, id }, s.system);
        return {
          system: { ...s.system, nodes: plan.nodes, cables: plan.cables },
        };
      });
      // Re-aim before routing: `optimalTerminalOverrides` reads the first waypoint as
      // the approach direction, so routing first leaves the terminal on the wrong side.
      get().recomputeTerminalsForCable(id);
      get().tidyCableLayoutsForCables([id], { skipHistory: true });
      return id;
    });
    recordExemptAddition(get().system, "wire", "unknown");
    return id;
  },

  updateCable: (id, partial) => {
    if (!requestSubscriptionEditing()) return;
    // A bare `fuseAmpOverride` is a manual pick; callers that mean auto pass `fuseAmpAuto`.
    const next: Partial<Cable> =
      "fuseAmpOverride" in partial && !("fuseAmpAuto" in partial)
        ? { ...partial, fuseAmpAuto: false }
        : partial;
    set((s) => ({
      system: {
        ...s.system,
        cables: s.system.cables.map((c) =>
          c.id === id ? clearLengthGuess(c, { ...c, ...next }) : c,
        ),
      },
    }));
  },

  updateCableWaypoints: (id, waypoints) => {
    if (!requestSubscriptionEditing()) return;
    set((s) => {
      const cables = s.system.cables.map((c) => {
        if (c.id !== id) return c;
        if (waypoints && waypoints.length > 0) return { ...c, waypoints };
        const { waypoints: _w, ...bare } = c;
        return bare as Cable;
      });
      const tentative: System = { ...s.system, cables };
      const updates = terminalOverrideUpdates(tentative, [id]);
      return {
        system: {
          ...tentative,
          nodes: applyTerminalOverrideUpdates(tentative.nodes, updates),
        },
      };
    });
  },

  repointCable: (id, endpoints) => {
    if (!requestSubscriptionEditing()) return;
    set((s) => {
      const moved = s.system.cables.map((c) =>
        c.id === id ? { ...c, ...endpoints } : c,
      );
      const tentativeSystem: System = { ...s.system, cables: moved };
      const cables = refreshAutoFuses(tentativeSystem);
      return { system: { ...s.system, cables } };
    });
    get().tidyCableLayoutsForCables([id], { skipHistory: true });
  },

  autoLayoutComponents: (ids, opts) => {
    if (!useFeatureFlags.getState().flags.componentAutoLayout || !requestSubscriptionEditing()) return;
    const system = get().system;
    const planned = getLayoutEngine().arrange(system, ids);
    if (planned === system) return;
    const apply = () => set({ system: planned });
    if (opts?.skipHistory) apply();
    else historied(apply);
  },

  resetCableLayouts: (ids) => {
    if (!requestSubscriptionEditing()) return;
    if (ids.length === 0) return;
    get().tidyCableLayouts(ids);
  },

  tidyCableLayoutsForNodes: (nodeIds, opts) => {
    if (!requestSubscriptionEditing()) return;
    if (nodeIds.length === 0) return;
    const { nodes, cables } = get().system;
    const affected = new Set(nodeIds);
    let grew = true;
    while (grew) {
      grew = false;
      for (const n of nodes) {
        if (n.parentId && affected.has(n.parentId) && !affected.has(n.id)) {
          affected.add(n.id);
          grew = true;
        }
      }
    }
    const seed = new Set<string>();
    for (const c of cables) {
      if (affected.has(c.fromNodeId) || affected.has(c.toNodeId)) seed.add(c.id);
    }
    // A cable can run through a body with neither endpoint moved; matching endpoints misses it.
    for (const id of cablesThroughNodes(nodes, cables, affected)) seed.add(id);
    if (seed.size === 0) return;
    const expanded = expandCableTidyScope(nodes, cables, seed);
    get().tidyCableLayouts(Array.from(expanded), opts);
  },

  tidyCableLayoutsForCables: (cableIds, opts) => {
    if (!requestSubscriptionEditing()) return;
    if (cableIds.length === 0) return;
    const { nodes, cables } = get().system;
    const expanded = expandCableTidyScope(nodes, cables, new Set(cableIds));
    if (expanded.size === 0) return;
    get().tidyCableLayouts(Array.from(expanded), opts);
  },

  tidyCableLayouts: (ids, opts) => {
    if (!requestSubscriptionEditing()) return;
    if (ids.length === 0) return;
    const system = get().system;
    const planned = getLayoutEngine().route(system, ids);
    if (planned === system) return;
    const apply = () => set({ system: planned });
    if (opts?.skipHistory) apply();
    else historied(apply);
  },

  removeCables: (ids) => {
    if (!requestSubscriptionEditing()) return;
    if (ids.length === 0) return;
    historied(() => {
    const idSet = new Set(ids);
    // Captured before the removal — these cables are about to be gone.
    const touchedNodes = new Set<string>();
    for (const c of get().system.cables) {
      if (!idSet.has(c.id)) continue;
      touchedNodes.add(c.fromNodeId);
      touchedNodes.add(c.toNodeId);
    }
    set((s) => {
      const baseCables = s.system.cables.filter((c) => !idSet.has(c.id));
      const { nodes, cables } = pruneOrphanedJunctions(s.system.nodes, baseCables);
      return {
        system: { ...s.system, nodes, cables },
        selection: filterSelectionToAlive(s.selection, nodes, cables),
      };
    });
    if (touchedNodes.size > 0) {
      get().tidyCableLayoutsForNodes(Array.from(touchedNodes), {
        skipHistory: true,
      });
    }
    });
  },

  setSelection: (sel) =>
    set((s) => {
      const nextNodeIds = sel.nodeIds ?? s.selection.nodeIds;
      const nextCableIds = sel.cableIds ?? s.selection.cableIds;
      if (
        sameIds(nextNodeIds, s.selection.nodeIds) &&
        sameIds(nextCableIds, s.selection.cableIds)
      ) {
        return s;
      }
      return {
        selection: { nodeIds: nextNodeIds, cableIds: nextCableIds },
        mobileDetailsOpen: false,
      };
    }),

  clearSelection: () =>
    set((s) =>
      s.selection.nodeIds.length === 0 && s.selection.cableIds.length === 0
        ? s
        : { selection: { nodeIds: [], cableIds: [] }, mobileDetailsOpen: false },
    ),

  selectAll: () => {
    const { system } = get();
    set({
      selection: {
        nodeIds: system.nodes.map((n) => n.id),
        cableIds: system.cables.map((c) => c.id),
      },
    });
  },

  duplicateSelection: () => {
    if (!requestSubscriptionEditing()) return { nodeIds: [], cableIds: [] };
    const { selection, system } = get();
    if (selection.nodeIds.length === 0 && selection.cableIds.length === 0) {
      return { nodeIds: [], cableIds: [] };
    }
    const selected = new Set(selection.nodeIds);
    const sourceNodes = system.nodes.filter((n) => selected.has(n.id));
    const plan = cloneNodesAndCables(sourceNodes, system.cables, 24);
    if (
      equipmentChangeIsBlocked({
        nextNodes: [...system.nodes, ...plan.nodes],
        operation: "duplicate",
        source: "duplicate",
      })
    ) {
      return { nodeIds: [], cableIds: [] };
    }
    const result = historied(() => {
      set((s) => {
        return {
          system: {
            ...s.system,
            nodes: [...s.system.nodes, ...plan.nodes],
            cables: [...s.system.cables, ...plan.cables],
          },
          selection: {
            nodeIds: plan.nodes.map((n) => n.id),
            cableIds: plan.cables.map((c) => c.id),
          },
        };
      });
      return {
        nodeIds: plan.nodes.map((n) => n.id),
        cableIds: plan.cables.map((c) => c.id),
      };
    });
    const after = get().system;
    recordEquipmentChange(system, after, "duplicate", "duplicate");
    recordExemptNodes(plan.nodes, after, "duplicate");
    return result;
  },

  copySelection: () => {
    const { selection, system } = get();
    const snapshot = snapshotSelectionToClipboard(selection.nodeIds, system);
    if (snapshot && snapshot.nodes.length > 0) set({ lastCopyAt: Date.now() });
  },

  pasteFromClipboard: () => {
    if (!requestSubscriptionEditing()) return { nodeIds: [], cableIds: [] };
    const before = get().system;
    const clipboard = getClipboard();
    if (!clipboard) return { nodeIds: [], cableIds: [] };
    if (
      equipmentChangeIsBlocked({
        nextNodes: [...before.nodes, ...clipboard.nodes],
        operation: "paste",
        source: "paste",
      })
    ) {
      return { nodeIds: [], cableIds: [] };
    }
    const liveIds = new Set(before.nodes.map((n) => n.id));
    const plan = planClipboardPaste(liveIds);
    if (!plan) return { nodeIds: [], cableIds: [] };
    const result = historied(() => {
      set((s) => {
        return {
          system: {
            ...s.system,
            nodes: [...s.system.nodes, ...plan.nodes],
            cables: [...s.system.cables, ...plan.cables],
          },
          selection: {
            nodeIds: plan.nodes.map((n) => n.id),
            cableIds: plan.cables.map((c) => c.id),
          },
        };
      });
      return {
        nodeIds: plan.nodes.map((n) => n.id),
        cableIds: plan.cables.map((c) => c.id),
      };
    });
    const after = get().system;
    recordEquipmentChange(before, after, "paste", "paste");
    recordExemptNodes(plan.nodes, after, "paste");
    return result;
  },

  nudgeSelection: (dx, dy) => {
    if (!requestSubscriptionEditing()) return;
    const { selection } = get();
    if (selection.nodeIds.length === 0) return;
    const ids = new Set(selection.nodeIds);
    set((s) => ({
      system: {
        ...s.system,
        nodes: s.system.nodes.map((n) =>
          ids.has(n.id)
            ? ({ ...n, position: { x: n.position.x + dx, y: n.position.y + dy } } as SystemNode)
            : n,
        ),
      },
    }));
  },

  setPendingConnection: (p) => set({ pendingConnection: p }),
  cancelPendingConnection: () => set({ pendingConnection: null }),
  confirmPendingConnection: (opts) => {
    if (!requestSubscriptionEditing()) return null;
    const { pendingConnection: p, system } = get();
    if (!p) return null;
    // Static accessor, not a hook: `lib/` stays React-free.
    const autoSizeTargetPct = useAutoSizeVdropTarget.getState().target;
    const plan = planCableConfirmation(p, opts, system, undefined, autoSizeTargetPct);
    if (!plan) return null;

    const id = get().addCable(plan.cable);
    set({ pendingConnection: null, selection: { nodeIds: [], cableIds: [id] } });
    return [id];
  },

  splitCableWithJunction: (params) => {
    if (!requestSubscriptionEditing()) return null;
    const plan = planCableSplit(params, get().system);
    if (!plan) return null;
    return historied(() => {
      set((s) => ({
        system: {
          ...s.system,
          nodes: [...s.system.nodes, plan.junction],
          cables: [
            ...s.system.cables.filter((c) => c.id !== plan.removedCableId),
            plan.c1,
            plan.c2,
            plan.c3,
          ],
        },
        selection: { nodeIds: [], cableIds: [plan.newCableId] },
      }));
      get().recomputeTerminalsForCables(plan.newCableIds);
      get().tidyCableLayoutsForNodes([plan.junctionId], { skipHistory: true });
      return { junctionId: plan.junctionId, newCableId: plan.newCableId };
    });
  },

  replaceLegacyMicrowaveWithAc: (nodeId, voltage) => {
    if (!requestSubscriptionEditing()) return;
    const before = get().system;
    const plan = planLegacyMicrowaveAcConversion(before, nodeId, voltage);
    if (!plan) return;
    historied(() => set({ system: {
      ...before,
      nodes: before.nodes.map(node => node.id === nodeId ? plan.node : node),
      cables: plan.updatedCables,
    } }));
    recordEquipmentChange(before, get().system, "replace", "replace");
  },

  configureCustomInlineFuse: (nodeId, rating, family) => {
    if (!requestSubscriptionEditing()) return;
    const before = get().system;
    const node = before.nodes.find(n => n.id === nodeId);
    if (node?.kind !== "component") return;
    const configured = configureInlineFuse(node, rating, family);
    if (!configured) return;
    historied(() => set({ system: {
      ...before,
      nodes: before.nodes.map(n => n.id === nodeId ? configured : n),
    } }));
  },

  configureCustomDcFuseBlock: (nodeId, family) => {
    if (!requestSubscriptionEditing()) return;
    const before = get().system;
    const node = before.nodes.find(n => n.id === nodeId);
    if (node?.kind !== "component") return;
    const configured = configureDcFuseBlock(node, before.cables, family);
    if (!configured) return;
    historied(() => set({ system: {
      ...before,
      nodes: before.nodes.map(n => n.id === nodeId ? configured.node : n),
      cables: configured.cables,
    } }));
  },

  replaceComponentDefinition: (nodeId, definitionId) => {
    if (!requestSubscriptionEditing()) return;
    const before = get().system;
    const plan = planComponentReplace(before, nodeId, definitionId);
    if (!plan) return;
    const nextNodes = before.nodes.map((node) =>
      node.id === nodeId ? plan.node : node,
    );
    if (
      equipmentChangeIsBlocked({
        nextNodes,
        operation: "replace",
        source: "replace",
        attemptedDefinition: getDefinition(definitionId),
        attemptedDefinitionId: definitionId,
      })
    ) {
      return;
    }
    historied(() => {
      set((s) => ({
        system: {
          ...s.system,
          nodes: s.system.nodes.map((n) => (n.id === nodeId ? plan.node : n)),
          cables: plan.updatedCables,
        },
      }));
      get().recomputeTerminalsForCables(
        plan.updatedCables
          .filter((c) => c.fromNodeId === nodeId || c.toNodeId === nodeId)
          .map((c) => c.id),
      );
      get().tidyCableLayoutsForNodes([nodeId], { skipHistory: true });
      set((s) => ({ system: { ...s.system, cables: refreshAutoFuses(s.system) } }));
    });
    const after = get().system;
    recordEquipmentChange(before, after, "replace", "replace");
    const oldNode = before.nodes.find((node) => node.id === nodeId);
    const newNode = after.nodes.find((node) => node.id === nodeId);
    if (oldNode && newNode && equipmentNodeExemption(oldNode) !== equipmentNodeExemption(newNode)) {
      const exemption = equipmentNodeExemption(newNode);
      if (exemption) recordExemptAddition(after, exemption, "replace");
    }
  },

  convertJunctionToBusbar: (nodeId, polarity) =>
    requestSubscriptionEditing() ? historied(() => {
      const plan = planJunctionToBusbar(nodeId, polarity, get().system);
      if (!plan) return;
      set((s) => ({
        system: {
          ...s.system,
          nodes: s.system.nodes.map((n) => (n.id === nodeId ? plan.newNode : n)),
          cables: plan.updatedCables,
        },
      }));
      get().tidyCableLayoutsForNodes([nodeId], { skipHistory: true });
    }) : undefined,

  convertJunctionToFusebox: (nodeId) =>
    requestSubscriptionEditing() ? historied(() => {
      const plan = planJunctionToBusbar(nodeId, "positive", get().system, {
        asFusebox: true,
      });
      if (!plan) return;
      set((s) => ({
        system: {
          ...s.system,
          nodes: s.system.nodes.map((n) => (n.id === nodeId ? plan.newNode : n)),
          cables: plan.updatedCables,
        },
      }));
      get().tidyCableLayoutsForNodes([nodeId], { skipHistory: true });
    }) : undefined,

  convertBusbarToJunction: (nodeId) =>
    requestSubscriptionEditing() ? historied(() => {
      const plan = planBusbarToJunction(nodeId, get().system);
      if (!plan) return;
      set((s) => ({
        system: {
          ...s.system,
          nodes: s.system.nodes.map((n) => (n.id === nodeId ? plan.newNode : n)),
          cables: plan.updatedCables,
        },
      }));
      get().tidyCableLayoutsForNodes([nodeId], { skipHistory: true });
    }) : undefined,

  recomputeTerminalsForCable: (cableId) => {
    if (!requestSubscriptionEditing()) return;
    get().recomputeTerminalsForCables([cableId]);
  },

  recomputeTerminalsForCables: (cableIds) => {
    if (!requestSubscriptionEditing()) return;
    const updates = terminalOverrideUpdates(get().system, cableIds);
    if (updates.length === 0) return;
    set((s) => ({
      system: {
        ...s.system,
        nodes: applyTerminalOverrideUpdates(s.system.nodes, updates),
      },
    }));
  },

  setTerminalOverride: (nodeId, terminalId, override) => {
    if (!requestSubscriptionEditing()) return;
    set((s) => {
      const nodes = s.system.nodes.map((n) => {
        if (n.id !== nodeId || n.kind !== "component") return n;
        return {
          ...n,
          terminalOverrides: {
            ...(n.terminalOverrides ?? {}),
            [terminalId]: override,
          },
        };
      });
      const cables = pruneStaleWaypointsForNode(nodes, s.system.cables, nodeId);
      return { system: { ...s.system, nodes, cables } };
    });
  },

  clearTerminalOverride: (nodeId, terminalId) => {
    if (!requestSubscriptionEditing()) return;
    historied(() => {
      set((s) => {
        const nodes = s.system.nodes.map((n) => {
          if (n.id !== nodeId || n.kind !== "component") return n;
          const cur = n.terminalOverrides ?? {};
          if (!(terminalId in cur)) return n;
          const { [terminalId]: _, ...rest } = cur;
          return { ...n, terminalOverrides: rest };
        });
        const cables = pruneStaleWaypointsForNode(nodes, s.system.cables, nodeId);
        return { system: { ...s.system, nodes, cables } };
      });
    });
  },

  setInspectorOpen: (open) => set({ inspectorOpen: open }),
  setPaletteOpen: (open) => set({ paletteOpen: open }),
  setMobileScenarioOpen: (open) => set({ mobileScenarioOpen: open }),
  setMobileDetailsOpen: (open) => set({ mobileDetailsOpen: open }),
  setMode: (mode) => set({ mode }),
  toggleMode: () => set({ mode: get().mode === "edit" ? "simulate" : "edit" }),
  ...createSimulateActions(set),

  loadSystem: (s, opts) => {
    if (opts?.enforceEquipmentLimit && !requestSubscriptionEditing()) return false;
    const result = migrate(s);
    if (!result.ok) {
      console.warn("loadSystem: rejecting incompatible payload", result.error);
      return false;
    }
    // Studs, terminal placements and routes belong to the saved drawing.
    const loadedSystem = result.system;
    const before = get().system;
    const source = opts?.source ?? "unknown";
    if (
      opts?.enforceEquipmentLimit &&
      equipmentChangeIsBlocked({
        nextNodes: loadedSystem.nodes,
        operation: "generate",
        source,
      })
    ) {
      return false;
    }
    set({
      system: loadedSystem,
      equipmentLimitAttempt: null,
      equipmentLimitJourneyId: nanoid(12),
      selection: { nodeIds: [], cableIds: [] },
      past: [],
      future: [],
    });
    // Only an explicit layout request re-routes a newly generated/imported drawing.
    // Opening an ordinary saved project must not change its layout or connections.
    const cableIds = get().system.cables.map((c) => c.id);
    if (source === "ai_system" && useFeatureFlags.getState().flags.componentAutoLayout) {
      get().autoLayoutComponents(get().system.nodes.map(node => node.id), { skipHistory: true });
    } else if (cableIds.length > 0 && opts?.preserveLayout === false) {
      get().tidyCableLayouts(cableIds, { skipHistory: true });
    }
    if (opts?.enforceEquipmentLimit) {
      const after = get().system;
      recordEquipmentChange(before, after, "generate", source);
      recordExemptNodes(after.nodes, after, source);
    }
    return true;
  },
  applyAiSystemEdit: (next, added) => {
    if (!requestSubscriptionEditing()) return false;
    const result = migrate(next);
    if (!result.ok) {
      // `applySystemEdit` already gated on this; reaching here is a planner bug.
      console.warn("applyAiSystemEdit: rejecting incompatible system", result.error);
      return false;
    }
    const before = get().system;
    if (
      equipmentChangeIsBlocked({
        nextNodes: result.system.nodes,
        operation: "generate",
        source: "ai_edit",
      })
    ) {
      return false;
    }
    historied(() => {
      set({
        system: result.system,
        selection: { nodeIds: added.nodeIds, cableIds: [] },
      });
      if (added.nodeIds.length && useFeatureFlags.getState().flags.componentAutoLayout) {
        get().autoLayoutComponents(added.nodeIds, { skipHistory: true });
      } else if (added.cableIds.length) {
        get().recomputeTerminalsForCables(added.cableIds);
        get().tidyCableLayoutsForCables(added.cableIds, { skipHistory: true });
      }
    });
    const after = get().system;
    recordEquipmentChange(before, after, "generate", "ai_edit");
    const addedNodes = after.nodes.filter((node) => added.nodeIds.includes(node.id));
    recordExemptNodes(addedNodes, after, "ai_edit");
    return true;
  },

  newSystem: () => {
    if (!requestSubscriptionEditing()) return;
    const { equipmentLimitAttempt, equipmentLimitLastHit } = get();
    const handoffId =
      equipmentLimitAttempt?.checkoutAttemptId ??
      equipmentLimitLastHit?.checkoutAttemptId;
    if (handoffId) {
      clearEquipmentLimitAuthHandoff({ checkoutAttemptId: handoffId });
    }
    set({
      system: emptySystem(),
      equipmentLimitAttempt: null,
      equipmentLimitLastHit: null,
      equipmentLimitJourneyId: nanoid(12),
      selection: { nodeIds: [], cableIds: [] },
      past: [],
      future: [],
    });
  },
  setSystemName: (name) => {
    if (!requestSubscriptionEditing()) return;
    set((s) => ({ system: { ...s.system, name } }));
  },
  setDesignProfile: (id) => {
    if (!requestSubscriptionEditing() || !DESIGN_PROFILES.some(profile => profile.id === id && profile.status === "available")) return;
    const { system } = get();
    if ((system.designProfileId ?? resolveDesignProfile(system).id) === id) return;
    historied(() => {
      const next = { ...system, designProfileId: id };
      const refreshed = refreshAutoFuses(next, getDefinition, useAutoSizeVdropTarget.getState().target);
      // A profile switch cannot remove a manual declaration, including an
      // invalid legacy AC fuse that diagnostics still need to show.
      const cables = next.cables.map((cable, index) => cable.fuseAmpAuto === true ? refreshed[index] : cable);
      set({ system: { ...next, cables } });
    });
  },
  setIssueReviewed: (issue, reviewed) => {
    if (!requestSubscriptionEditing()) return;
    const { system, simulateState } = get();
    const key = issueReviewKey(issue);
    const reviews = { ...system.issueReviews };
    if (reviewed) {
      const target = useAutoSizeVdropTarget.getState().target;
      const current = runAllDiagnostics(system, getDefinition,
        simulateState.componentStates, simulateState.batterySoc, target,
        simulateState.loadStates).issues.find(i => issueReviewKey(i) === key);
      if (!current || !canReviewIssue(current)) return;
      const next = issueReviewFingerprint(system, current, getDefinition, target);
      if (reviews[key] === next) return;
      reviews[key] = next;
    } else {
      if (!(key in reviews)) return;
      delete reviews[key];
    }
    historied(() => set({ system: { ...system, issueReviews: reviews } }));
  },

  freezeBuild: () => {
    if (!requestSubscriptionEditing()) return { wiresFrozen: 0, fusesRefreshed: 0 };
    const autoSizeTargetPct = useAutoSizeVdropTarget.getState().target;
    const plan = planFreezeBuild(get().system, undefined, autoSizeTargetPct);
    if (plan.wiresFrozen === 0 && plan.fusesRefreshed === 0) {
      return { wiresFrozen: 0, fusesRefreshed: 0 };
    }
    return historied(() => {
      set((s) => ({ system: { ...s.system, cables: plan.cables } }));
      return { wiresFrozen: plan.wiresFrozen, fusesRefreshed: plan.fusesRefreshed };
    });
  },

  unfreezeBuild: () => {
    if (!requestSubscriptionEditing()) return { wiresCleared: 0 };
    const plan = planUnfreezeBuild(get().system);
    if (plan.wiresCleared === 0) return { wiresCleared: 0 };
    return historied(() => {
      set((s) => ({ system: { ...s.system, cables: plan.cables } }));
      return { wiresCleared: plan.wiresCleared };
    });
  },

  undo: () => {
    if (!requestSubscriptionEditing()) return;
    const { past, system, future } = get();
    if (past.length === 0) return;
    const previous = past[past.length - 1];
    set({
      past: past.slice(0, -1),
      future: [{ system: cloneSystem(system) }, ...future],
      system: previous.system,
    });
    recordEquipmentChange(system, previous.system, "undo", "undo");
  },

  redo: () => {
    if (!requestSubscriptionEditing()) return;
    const { past, system, future } = get();
    if (future.length === 0) return;
    const next = future[0];
    set({
      past: [...past, { system: cloneSystem(system) }],
      future: future.slice(1),
      system: next.system,
    });
    recordEquipmentChange(system, next.system, "redo", "redo");
  },
  };
});
